// useDeleteTicket hook

import { useCallback, useState } from "react";

import { useUserContext } from "@hooks/use-auth-context";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import { isValidId } from "@hooks/use-get/utils";
import { useScreenAlert } from "@hooks/use-screen-alert";

export const useDeleteTicket = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const { setError } = useScreenAlert();

    const { user } = useUserContext();
    const { getApiUrl } = useMultiSiteContext();
    const { refreshUserToken } = useRefreshTokenMutation();

    const deleteTicket = useCallback(
        async (id: string | number) => {
            setLoading(true);

            // Validate ID to prevent injection attacks
            if (!isValidId(id)) {
                setError("Invalid ticket ID.");
                setLoading(false);
                return;
            }

            const apiUrl = getApiUrl(`wp-json/wp/v2/ticket/${id}`);

            try {
                const response = await fetchWithTokenRefresh(
                    apiUrl,
                    {
                        method: "DELETE",
                        headers: {
                            Authorization: `Bearer ${user?.authToken ?? ""}`,
                            "Content-Type": "application/json",
                        },
                    },
                    () => refreshUserToken(user?.refreshToken ?? "")
                );

                if (!response.ok) {
                    const body = await response.json().catch(() => null);
                    const serverMessage: string | undefined = body?.message;
                    setError(serverMessage ?? "Failed to delete ticket. Please try again.");
                    console.error("[useDeleteTicket] HTTP", response.status, serverMessage ?? "");
                    setLoading(false);
                    return;
                }

                setLoading(false);
                return response;
            } catch (err) {
                setError("Failed to delete ticket. Please try again.");
                console.error("[useDeleteTicket]", err);
                setLoading(false);
            }
        },
        [getApiUrl, user?.authToken, user?.refreshToken, refreshUserToken, setError]
    );

    return {
        deleteTicket,
        loading,
    };
};
